/**
 * proxyAuth — the browser tells the paid-model proxies WHO is calling
 * (security remediation slice 6, docs/PLAN-security-remediation-20260915.md §2.2).
 *
 * anthropicProxy and perplexityProxy spend the company's model key. Until this
 * slice no browser call carried the signed-in user's identity, so the server
 * could neither refuse a stranger nor check that the workspace a call bills
 * (the X-Account-Id hint, ./accountHint) belongs to the caller. Every proxy
 * fetch now spreads proxyAuthHeaders(): `Authorization: Bearer <Firebase ID
 * token>` for a signed-in, non-anonymous user — and NOTHING otherwise.
 *
 * Never throws and never blocks a call: a signed-out or still-hydrating tab
 * sends today's tokenless request, which the server admits in `log` mode
 * (functions/src/proxyCallerGate.ts) and refuses with "refresh and sign in"
 * once `ops/proxyAuth.mode` is `enforce`. The token is the IDENTITY; the
 * X-Account-Id hint stays the billing ATTRIBUTION — the server binds the two.
 *
 * The wait-for-auth is idTokenFor (./billingSnapshotCache): the current user's
 * token at once, else the first auth-state tick within 4 s, else null.
 * Pinned by tests/unit/proxyCallersAttachIdToken.test.ts, whose denominator is
 * every proxy fetch site under src/, read off the tree.
 */
import { getAuth } from 'firebase/auth';
import { idTokenFor } from './billingSnapshotCache';

export const PROXY_AUTH_HEADER = 'Authorization';

/** `{ Authorization: 'Bearer …' }` for a signed-in non-anonymous user; `{}` otherwise. Never throws. */
export async function proxyAuthHeaders(): Promise<Record<string, string>> {
  try {
    const auth = getAuth();
    const token = await idTokenFor(auth);
    if (!token) return {};
    // An anonymous-provider identity is no identity for this purpose (the
    // slice-3 posture): the server counts it as unverified, so send nothing.
    if (auth.currentUser?.isAnonymous) return {};
    return { [PROXY_AUTH_HEADER]: `Bearer ${token}` };
  } catch {
    return {};
  }
}
